import type { ProgrammaticLandingPageConfig } from '../../types'
import {
  STORAGE_REFERENCE_KWP,
  SELF_CONSUMPTION_WITHOUT_BATTERY,
  SELF_CONSUMPTION_BATTERY_BONUS,
  SELF_CONSUMPTION_WITH_BATTERY,
  BATTERY_COST_PER_KWH_RANGE_EUR,
} from '../storageSizeFacts'

export const KWH_12_BATERIA_SOLAR: ProgrammaticLandingPageConfig = {
  slug: 'bateria-solar-12-kwh-precio',
  lang: 'es',
  topicId: 'kwh-speicher-12',
  title: 'Batería solar de 12 kWh: precio y valores orientativos 2026',
  metaDescription:
    '¿Cuánto cuesta una batería solar de 12 kWh? Para quién es adecuada, valores orientativos de ' +
    'autoconsumo y comparación gratuita de presupuestos para instalación + batería.',
  keywords: [
    'batería solar 12 kWh precio',
    'acumulador solar 12 kWh coste',
    'batería fotovoltaica 12 kWh',
    'cuánto cuesta una batería de 12 kWh',
  ],
  eyebrow: 'Tamaño de batería: 12 kWh',
  h1: 'Batería solar de 12 kWh: precio y valores orientativos',
  intro:
    'Una batería de 12 kWh encaja con hogares de consumo algo mayor que el estándar, combinada ' +
    `con una instalación de referencia de ${STORAGE_REFERENCE_KWP} kWp. Aquí encontrará valores ` +
    'orientativos sobre el tamaño de hogar adecuado, el autoconsumo con batería y justo debajo ' +
    'una calculadora con el precio medio ya calculado para esta combinación a partir de ' +
    'presupuestos reales de empresas instaladoras.',
  calculatorPrefill: { kind: 'kwp+storage', kwp: STORAGE_REFERENCE_KWP, kwh: 12 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: '¿Para quién es adecuada una batería de 12 kWh?' },
    {
      type: 'paragraph',
      text:
        'Valor orientativo: 12 kWh es adecuado para hogares con mayor consumo por la tarde/noche ' +
        'o con carga ocasional de un coche eléctrico. Con bomba de calor o carga habitual de un ' +
        'coche eléctrico, 15 o 20 kWh suele ser la etapa más adecuada.',
    },
    { type: 'heading', text: 'Autoconsumo con batería solar' },
    {
      type: 'paragraph',
      text:
        `Sin batería, el autoconsumo de una instalación de ${STORAGE_REFERENCE_KWP} kWp ronda ` +
        `normalmente el ${SELF_CONSUMPTION_WITHOUT_BATTERY * 100} %; con batería solar, unos ` +
        `${SELF_CONSUMPTION_BATTERY_BONUS * 100} puntos más en el modelo de cálculo, hasta rondar ` +
        `el ${SELF_CONSUMPTION_WITH_BATTERY * 100} % - un perfil de carga adicional de coche ` +
        'eléctrico puede seguir aumentando el porcentaje hasta un límite del 85 %. Un cálculo ' +
        'exacto según su consumo lo ofrece la calculadora de dimensionamiento.',
    },
    { type: 'heading', text: 'Suministro de emergencia' },
    {
      type: 'paragraph',
      text:
        'Con 12 kWh, muchas cajas de respaldo para suministro de emergencia alcanzan para una ' +
        'parte notablemente mayor del hogar durante un periodo más largo - el tiempo exacto ' +
        'depende del consumo real de los aparatos conectados. Si un presupuesto incluye o no esta ' +
        'función depende de cada instalador - conviene preguntarlo directamente.',
    },
    { type: 'heading', text: 'Coste de la batería y ayudas' },
    {
      type: 'paragraph',
      text:
        'El coste de las baterías varía mucho según el fabricante y la complejidad de la ' +
        'instalación - la investigación de mercado de 2026 muestra un rango de entre ' +
        `${BATTERY_COST_PER_KWH_RANGE_EUR.min} y ${BATTERY_COST_PER_KWH_RANGE_EUR.max.toLocaleString('es-ES')} ` +
        '€ por kWh. En Alemania existen ayudas específicas para baterías según la región (p. ej. ' +
        'el programa del estado federado de Berlín, con subvención para baterías); en Austria, el ' +
        'sistema EAG añade 150 €/kWh para el acumulador - un cálculo exacto para su combinación lo ' +
        'ofrece la calculadora de abajo.',
    },
  ],
  faq: [
    {
      q: '¿Cuándo conviene más 12 kWh que 10 kWh?',
      a: 'Con mayor consumo por la tarde/noche o si de vez en cuando se carga un coche eléctrico en casa, 12 kWh suele cubrir mejor la necesidad adicional que 10 kWh.',
    },
    {
      q: '¿Merece la pena 12 kWh sin bomba de calor ni coche eléctrico?',
      a: 'En general no está óptimamente aprovechada - sin estos grandes consumidores, 10 kWh suele ser suficiente para la mayoría de los hogares. Una recomendación exacta la ofrece la calculadora de dimensionamiento.',
    },
    {
      q: '¿Con qué tamaño de instalación se combina normalmente una batería de 12 kWh?',
      a: `Aquí se toma como referencia una instalación de ${STORAGE_REFERENCE_KWP} kWp; en la práctica también se combina a menudo con 12 kWp. La calculadora ofrece una recomendación exacta para su tejado.`,
    },
    {
      q: '¿Es suficiente 12 kWh para ser autosuficiente durante la noche?',
      a: 'Sí, en la mayoría de los hogares incluso con un consumo por encima de la media. Una estimación exacta según su consumo real la ofrece la calculadora de dimensionamiento.',
    },
  ],
  primaryCta: { label: 'Solicitar presupuesto gratis ahora', action: 'customer' },
  secondaryCta: {
    label: 'Coste de una instalación fotovoltaica: guía completa',
    action: 'link',
    href: '/coste-instalacion-fotovoltaica',
  },
  relatedLinks: [
    { label: 'Batería solar de 10 kWh: precio y valores orientativos', href: '/bateria-solar-10-kwh-precio' },
    { label: 'Batería solar de 15 kWh: precio y valores orientativos', href: '/bateria-solar-15-kwh-precio' },
    { label: 'Instalación fotovoltaica de 12 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-12-kwp-precio' },
    { label: 'Cómo funciona la comparación de presupuestos', href: '/comparar-presupuestos-fotovoltaicos' },
  ],
}
